import { useState, useEffect, useMemo, Dispatch, SetStateAction, ReactElement } from 'react';

import Axios from '../../../helpers/http';
import { getFileName as defaultGetFileName } from '../helpers/filePickerHelpers';
import { defineFilesState, getRequests } from '../helpers/restHelpers';

import { File } from '../FilePicker';

interface Meta {
  initial: Array<File>;
}

interface Input {
  onFocus: (value: File[]) => void;
  onChange: (files: any) => void;
  onBlur: (value: File[]) => void;
  onDrop: () => void;
  value: Array<File>;
}
export interface HttpClient {
  // eslint-disable-next-line @typescript-eslint/ban-types
  all: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  post: Function;
}

interface FileProps {
  onChange: (files: any) => void;
  onDelete: (file: any) => void;
  onFocus: () => void;
  onBlur: () => void;
  isLoading: boolean;
  customError: string;
  value: File[];
  setMultiple: Dispatch<SetStateAction<boolean>>;
}

export interface RestFilesProps {
  httpClient: HttpClient;
  render: (fileProps: FileProps) => ReactElement | null;
  url: string;
  value?: Array<File>;
  files?: Array<File>;
  defaultFiles?: Array<File>;
  // eslint-disable-next-line @typescript-eslint/ban-types
  onError?: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  onRemove?: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  onUpload?: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  getFileName?: Function;

  /** @ignore */
  meta: Meta;
  /** @ignore */
  input: Input;
}

function RestFiles(props: RestFilesProps) {
  const { input, meta, defaultFiles, files, httpClient, url, onRemove, onUpload, onError, render } = props;
  const reduxFormValue = input.value || meta.initial;
  const getFileName = props.getFileName || defaultGetFileName;
  const [value, setValue] = useState<Array<File>>(defineFilesState(reduxFormValue, defaultFiles, files, props.value));
  const [loading, setLoading] = useState<boolean>(false);
  const [customError, setCustomError] = useState<string>('');
  const [multiple, setMultiple] = useState<boolean>(false);

  useEffect(() => {
    setValue(defineFilesState(input.value, null, files, props.value));
  }, [props.value, input.value, files]);

  const handleUpload = newFiles => {
    if (onUpload) {
      onUpload(newFiles);
    }
    if (input.onChange) {
      input.onChange(newFiles);
    }
  };

  const handleError = err => {
    if (err && err.response && err.response.data) {
      if (err.response.data.includes('<!DOCTYPE html>')) {
        setCustomError(err.toJSON().message);
      } else {
        setCustomError(err.response.data);
      }
    }

    if (onError) {
      onError(err);
    }
  };

  const onDelete = file => {
    const newFiles = value.filter(f => f.name !== file.name);
    setValue(newFiles);

    if (onRemove) {
      onRemove(newFiles);
    }
    if (input.onChange) {
      input.onChange(newFiles);
    }
  };

  const onFocus = () => {
    if (input.onFocus) {
      input.onFocus(value);
    }
  };

  const onBlur = () => {
    if (input.onBlur) {
      input.onBlur(value);
    }
  };

  const updateFiles = newFile => {
    if (multiple) {
      setValue(previousValue => {
        const newFiles = [...previousValue.filter(f => f.name !== newFile.name), newFile];
        handleUpload(newFiles);
        return newFiles;
      });
    } else {
      handleUpload([newFile]);
      setValue([newFile]);
    }
  };

  const chainRequests = requests => {
    setLoading(true);

    httpClient
      .all(requests)
      .then(response => {
        setCustomError('');

        response.forEach(resp => {
          const newFile = {
            ...resp.data,
            name: getFileName(resp.data),
          };

          updateFiles(newFile);
        });
      })
      .catch(handleError)
      .finally(() => setLoading(false));
  };

  const state = useMemo(() => {
    const onChange = files => {
      const requests = getRequests(files, httpClient, url);
      chainRequests(requests);
    };

    return {
      onChange,
      onDelete,
      onFocus,
      onBlur,
      isLoading: loading,
      customError,
      value,
      setMultiple,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, loading, multiple, customError]);

  return render(state);
}

RestFiles.defaultProps = {
  httpClient: Axios,
  input: {},
  meta: {},
};

RestFiles.displayName = 'RestFiles';

export default RestFiles;
